/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Named table scroll regions require keyboard focus for horizontal scrolling. */
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuSearch, LuArrowDownUp } from "react-icons/lu";
import {
  setCartSearch,
  setOrderSearch,
} from "../../../../reducers/userConfigSlice";
import {
  exportCollection,
  normalizeSearch,
} from "../../../../components/collectionTools";

export const orderCode = (row) =>
  row._cart
    ? "—"
    : (row.displayNumber ??
      (row.number != null
        ? `${row.SalesChannel?.identifier || ""}${row.number}`
        : "—"));
export const orderReference = (row) => (row._cart ? "—" : (row.number ?? "—"));
export const customerName = (row) =>
  row.Customer?.fullname || row.Customer?.fullName || "Cliente contado";
export const deliveryLabel = (row) => {
  const original = row.shippingMethod;
  const label = normalizeSearch(original || "").trim();
  if (["recoger en tienda", "recogida en tienda", "recogida"].includes(label))
    return "Recogida";
  if (["reparto a domicilio", "a domicilio", "domicilio"].includes(label))
    return "Domicilio";
  return original || "—";
};
export const orderAmount = (row) => {
  const value = row.amounts?.total;
  return value != null &&
    String(value).trim() !== "" &&
    Number.isFinite(Number(value))
    ? new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value))
    : "—";
};
const statuses = {
  pending: "Pendiente",
  inPreparation: "En preparación",
  prepared: "Preparado",
  inDelivery: "En ruta",
  delivered: "Entregado",
  cancelled: "Cancelado",
  abandoned: "Carrito abandonado",
};
const payments = {
  paid: "Pagado",
  pending: "Pendiente",
  refunded: "Devuelto",
  partiallyPaid: "Pago parcial",
};
const badge = (value, labels) => (
  <span className={`ordersBadge state-${value || "unknown"}`}>
    {labels[value] || value || "Sin información"}
  </span>
);
export const filterOrderRows = (
  rows,
  {
    period,
    search = "",
    tab = "all",
    channel = "",
    payment = "",
    now = Date.now(),
  },
) => {
  const cutoff = period === "all" ? null : new Date(now);
  if (cutoff) cutoff.setDate(cutoff.getDate() - (period === "7days" ? 7 : 30));
  const needle = normalizeSearch(search);
  return rows.filter(
    (row) =>
      (!cutoff || new Date(row.createdAt).getTime() >= cutoff.getTime()) &&
      (tab === "all" || row.status === tab) &&
      (!channel ||
        (row.salesChannelId ?? row.SalesChannel?.salesChannelId) === channel) &&
      (!payment || row.paymentStatus === payment) &&
      (!needle ||
        normalizeSearch(
          `${orderCode(row)} ${row.number ?? ""} ${row._cart ? (row.shoppingCartId ?? "") : ""} ${customerName(row)}`,
        ).includes(needle)),
  );
};
const tabs = [
  ["all", "Todos"],
  ["pending", "Pendientes"],
  ["inPreparation", "En preparación"],
  ["prepared", "Preparado"],
];
const dateLabel = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Fecha no disponible"
    : new Intl.DateTimeFormat("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
};

export const OrdersList = ({
  rows,
  open,
  period,
  onExportView,
  cartsOnly = false,
}) => {
  const setSearch = cartsOnly ? setCartSearch : setOrderSearch;
  const dispatch = useDispatch();
  const search = useSelector(
    (state) =>
      state.aplicationConfig.userConfig[
        cartsOnly ? "cartSearch" : "orderSearch"
      ] || "",
  );
  const channels =
    useSelector((state) => state.aplicationConfig.corporationData.channels) ||
    [];
  const [tab, setTab] = useState("all");
  const [channel, setChannel] = useState("");
  const [payment, setPayment] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(0);
  const all = useMemo(
    () =>
      cartsOnly
        ? rows.map((row) => ({ ...row, status: "abandoned", _cart: true }))
        : rows.filter((row) => !row._cart && Boolean(row.orderId)),
    [rows, cartsOnly],
  );
  const filtered = useMemo(
    () => filterOrderRows(all, { period, search, channel, payment }),
    [all, period, search, channel, payment],
  );
  const visible = useMemo(
    () =>
      filtered
        .filter((row) => tab === "all" || row.status === tab)
        .sort((a, b) => {
          const delta =
            (new Date(a.createdAt).getTime() || 0) -
            (new Date(b.createdAt).getTime() || 0);
          return sort === "oldest" ? delta : -delta;
        }),
    [filtered, tab, sort],
  );
  useEffect(() => {
    setPage(0);
  }, [period, search, channel, payment, tab, sort]);
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(visible.length / 20) - 1),
  );
  useEffect(() => {
    onExportView(
      visible.length
        ? () =>
            exportCollection(
              visible,
              [
                ...(cartsOnly
                  ? [
                      {
                        label: "ID de carrito",
                        value: (row) => row.shoppingCartId,
                      },
                    ]
                  : [
                      { label: "Número diario", value: orderCode },
                      { label: "Referencia", value: orderReference },
                    ]),
                { label: "Cliente", value: customerName },
                ...(!cartsOnly
                  ? [
                      {
                        label: "Canal de venta",
                        value: (row) => row.SalesChannel?.name,
                      },
                      { label: "Entrega", value: deliveryLabel },
                      {
                        label: "Pago",
                        value: (row) =>
                          payments[row.paymentStatus] || row.paymentStatus,
                      },
                      {
                        label: "Total con IVA",
                        value: (row) => row.amounts?.total,
                      },
                      {
                        label: "Estado",
                        value: (row) => statuses[row.status] || row.status,
                      },
                    ]
                  : [{ label: "Estado", value: () => "Carrito abandonado" }]),
                { label: "Fecha", value: (row) => row.createdAt },
              ],
              cartsOnly ? "carritos" : "pedidos",
            )
        : null,
    );
    return () => onExportView(null);
  }, [visible, onExportView, cartsOnly]);
  return (
    <div className="ordersListFrame">
      <div
        className="ordersTabs"
        aria-label={cartsOnly ? "Vistas de carritos" : "Vistas de pedidos"}
      >
        {(cartsOnly ? [["all", "Abandonados"]] : tabs).map(([id, label]) => (
          <button
            type="button"
            key={id}
            aria-pressed={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
            <span>
              {
                filtered.filter((row) => id === "all" || row.status === id)
                  .length
              }
            </span>
          </button>
        ))}
      </div>
      <div className="ordersListToolbar">
        <label className="ordersListSearch">
          <LuSearch aria-hidden="true" />
          <span className="visually-hidden">
            {cartsOnly
              ? "Buscar por cliente o ID de carrito"
              : "Buscar por número, referencia o cliente"}
          </span>
          <input
            type="search"
            placeholder={
              cartsOnly
                ? "Buscar por cliente o ID de carrito"
                : "Buscar por nº, referencia o cliente"
            }
            value={search}
            onChange={(e) => dispatch(setSearch(e.target.value))}
          />
        </label>
        <label className="ordersSort">
          <LuArrowDownUp aria-hidden="true" />
          <span className="visually-hidden">Ordenar por fecha</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Fecha · más reciente</option>
            <option value="oldest">Fecha · más antigua</option>
          </select>
        </label>
        <span className="ordersCount" role="status">
          {visible.length}{" "}
          {cartsOnly
            ? visible.length === 1
              ? "carrito"
              : "carritos"
            : visible.length === 1
              ? "registro"
              : "registros"}
        </span>
        {!cartsOnly && (
          <details className="ordersExtraFilters">
            <summary>Más filtros</summary>
            <div>
              <label>
                Canal de venta
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <option value="">Todos los canales</option>
                  {channels.map((item) => (
                    <option
                      key={item.salesChannelId}
                      value={item.salesChannelId}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Pago
                <select
                  value={payment}
                  onChange={(e) => setPayment(e.target.value)}
                >
                  <option value="">Todos los pagos</option>
                  {Object.entries(payments).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </details>
        )}
      </div>
      <div
        className="ordersTableScroll"
        role="region"
        aria-label={
          cartsOnly
            ? "Listado de carritos, desplazable horizontalmente"
            : "Listado de pedidos, desplazable horizontalmente"
        }
        tabIndex={0}
      >
        <table className={cartsOnly ? "ordersTable cartsTable" : "ordersTable"}>
          <caption className="visually-hidden">
            {cartsOnly
              ? "Carritos abandonados del periodo seleccionado"
              : "Pedidos del periodo seleccionado"}
          </caption>
          <thead>
            <tr>
              {(cartsOnly
                ? [
                    "ID de carrito",
                    "Cliente",
                    "Estado del carrito",
                    "Hora / fecha",
                  ]
                : [
                    "Nº diario",
                    "Referencia",
                    "Cliente",
                    "Canal de venta",
                    "Entrega",
                    "Estado del pago",
                    "Total",
                    "Estado del pedido",
                    "Hora / fecha",
                  ]
              ).map((label) => (
                <th scope="col" key={label}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible
              .slice(safePage * 20, (safePage + 1) * 20)
              .map((row, index) => (
                <tr
                  key={`${row._cart ? "cart" : "order"}-${row.orderId || row.shoppingCartId || row.id || index}`}
                >
                  <td data-label={cartsOnly ? "ID de carrito" : "Nº diario"}>
                    {cartsOnly ? (
                      <span title={row.shoppingCartId}>
                        {row.shoppingCartId || "—"}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="ordersOpen"
                          onClick={() => open({ ...row, id: row.orderId })}
                        >
                          {orderCode(row)}
                        </button>
                      </>
                    )}
                  </td>
                  {!cartsOnly && (
                    <td data-label="Referencia">{orderReference(row)}</td>
                  )}
                  <td data-label="Cliente" title={customerName(row)}>
                    {customerName(row)}
                  </td>
                  {!cartsOnly && (
                    <>
                      <td
                        data-label="Canal de venta"
                        title={row.SalesChannel?.name}
                      >
                        {row.SalesChannel?.name || "—"}
                      </td>
                      <td data-label="Entrega" title={row.shippingMethod}>
                        {deliveryLabel(row)}
                      </td>
                      <td data-label="Estado del pago">
                        {badge(row.paymentStatus, payments)}
                      </td>
                      <td data-label="Total" className="ordersMoney">
                        {orderAmount(row)}
                      </td>
                    </>
                  )}
                  <td
                    data-label={
                      cartsOnly ? "Estado del carrito" : "Estado del pedido"
                    }
                  >
                    {badge(row.status, statuses)}
                  </td>
                  <td data-label="Hora / fecha">
                    <time dateTime={row.createdAt}>
                      {dateLabel(row.createdAt)}
                    </time>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!visible.length && (
        <div className="ordersEmpty" role="status">
          No hay resultados para esta vista.
          <button
            type="button"
            onClick={() => {
              setTab("all");
              setChannel("");
              setPayment("");
              dispatch(setSearch(""));
            }}
          >
            Limpiar filtros
          </button>
        </div>
      )}
      {visible.length > 20 && (
        <nav
          className="ordersPagination"
          aria-label={cartsOnly ? "Páginas de carritos" : "Páginas de pedidos"}
        >
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
          >
            Anterior
          </button>
          <span>
            Página {safePage + 1} de {Math.ceil(visible.length / 20)}
          </span>
          <button
            type="button"
            disabled={(safePage + 1) * 20 >= visible.length}
            onClick={() => setPage(safePage + 1)}
          >
            Siguiente
          </button>
        </nav>
      )}
    </div>
  );
};
